import { httpCancelSession } from "api/admin/bitoshi-hour";
import { BitoshiHourForm } from "components/forms/BitoshiHourForm";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import ConfirmModal from "components/ui/ConfirmModal";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { get } from "utilities/network";

interface Session {
  id: number;
  name: string;
  isCancelled: boolean;
  startDateTime: string;
  endDateTime: string;
  data: string;
  eligibleCountries: string[];
  createdAt: string;
  updatedAt: string;
}

const EditBitoshiHour: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const sessionId = id ? parseInt(id, 10) : null;

  const { data: session, isLoading: isSessionLoading } = useQuery<Session>(
    ["session", sessionId],
    () => get(`admin/bitoshi-hour/${sessionId}`),
    {
      enabled: !!sessionId,
    }
  );

  const cancelMutation = useMutation(
    () => httpCancelSession(sessionId!),
    {
      onSuccess: () => {
        toast.success("Session cancelled successfully");
        setShowCancelModal(false);
        navigate("/bitoshi-hour");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ?? "Failed to cancel session"
        );
        setShowCancelModal(false);
      },
    }
  );

  if (!sessionId) {
    return (
      <Container>
        <AdminSidebar />
        <Main className="mx-5 mt-5 text-left max-w-[1000px]">
          <p className="text-red-600">Invalid session ID</p>
        </Main>
      </Container>
    );
  }

  if (isSessionLoading) {
    return (
      <Container>
        <AdminSidebar />
        <Main className="mx-5 mt-5 text-left max-w-[1000px]">
          <p>Loading...</p>
        </Main>
      </Container>
    );
  }

  if (!session) {
    return (
      <Container>
        <AdminSidebar />
        <Main className="mx-5 mt-5 text-left max-w-[1000px]">
          <p className="text-red-600">Session not found</p>
        </Main>
      </Container>
    );
  }

  const sessionData = JSON.parse(session.data);

  // Format datetime for datetime-local input
  const formatDatetimeLocal = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const defaultValues = {
    name: session.name,
    startDateTime: formatDatetimeLocal(session.startDateTime),
    endDateTime: formatDatetimeLocal(session.endDateTime),
    rate: {
      buy: sessionData.buy,
      sell: sessionData.sell,
      btcPercent: sessionData.btcPercent,
    },
    eligibleCountries: session.eligibleCountries,
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left max-w-[1000px]">
        <div className="flex justify-between items-center mb-10">
          <h2 className="text-2xl">Edit Bitoshi Hour</h2>
        </div>
        <BitoshiHourForm
          defaultValues={defaultValues}
          isEditMode={true}
          sessionId={session.id}
        />
        <ConfirmModal
          isOpen={showCancelModal}
          close={() => setShowCancelModal(false)}
          title="Cancel Session"
          message="Are you sure you want to cancel this Bitoshi Hour session? This action cannot be undone."
          onConfirm={() => cancelMutation.mutate()}
          confirmText="Yes, Cancel"
          status={cancelMutation.isLoading ? "pending" : ""}
        />
      </Main>
    </Container>
  );
};

export default EditBitoshiHour;
